import { useMemo } from 'react';
import { OverlayScrollArea } from '@odo/shared-ui';
import type { IteroOrder, IteroPatient } from '../../../../../services/iteroBrowse';
import { BROWSE_FORMATTERS } from '../browseFormatters';
import { CopyButton } from '../CopyButton';
import { FactTiles } from '../pane/FactTiles';
import { patientFacts, truncateMiddle } from './profileFormat';
import { ScanHistory } from './ScanHistory';
import { usePatientScanHistory } from './usePatientScanHistory';

const ITERO_ID_VISIBLE_CHARS = 22;

function IteroIdRow({ id }: { id: string }) {
    return (
        <div className="flex min-w-0 items-center gap-2 text-[12px]">
            <span className="shrink-0 text-gray-500">iTero ID</span>
            <code
                title={id}
                className="min-w-0 truncate rounded-md border border-gray-200 bg-white px-1.5 py-0.5 font-mono text-[11px] text-gray-700"
            >
                <span aria-hidden="true">{truncateMiddle(id, ITERO_ID_VISIBLE_CHARS)}</span>
                <span className="sr-only">{id}</span>
            </code>
            <CopyButton text={id} label="iTero patient ID" />
        </div>
    );
}

interface PatientProfileProps {
    patient: IteroPatient;
    // The order the admin just came back from; its card takes focus once the timeline shows.
    returnFocusRxId: string | null;
    onOpenOrder: (order: IteroOrder) => void;
}

export function PatientProfile({ patient, returnFocusRxId, onOpenOrder }: PatientProfileProps) {
    const chart = patient.chartNumber.trim();
    const id = patient.id.trim();
    const history = usePatientScanHistory(chart);
    const facts = useMemo(() => patientFacts(patient, BROWSE_FORMATTERS), [patient]);

    return (
        <OverlayScrollArea className="min-h-0 flex-1">
            <div className="flex flex-col gap-4 border-t border-gray-200 px-5 pb-6 pt-4">
                <FactTiles tiles={facts} label="Patient facts" />
                {id && <IteroIdRow id={id} />}
                <ScanHistory
                    hasChart={chart !== ''}
                    history={history}
                    returnFocusRxId={returnFocusRxId}
                    onOpenOrder={onOpenOrder}
                />
            </div>
        </OverlayScrollArea>
    );
}
